import { useCallback, useEffect, useState } from 'react';

import { Template } from '../services/TemplateService/Template.type';
import { lessonGameMetadata } from '../utils/lessonGameMetadata';
import { getTitle } from '../utils/templateUtils';
import { useBreakPoint } from './useBreakPoint';
import useCreateGameFromTemplate from './useCreateGameFromTemplate';
import useGameAutoSave from './useGameAutoSaver';
import { useMetrics } from './useMetrics';
import useRouterMatch from './useRouterMatch';

const usePresent = () => {
  const isDesktop = useBreakPoint(900);
  const metrics = useMetrics();
  const [isCreatingGameCurimotePressed, setIsCreatingGameCurimotePressed] = useState(
    false,
  );

  const { autoSave: autoSaveGame } = useGameAutoSave();
  const [isCreatingGamePresentPressed, setIsCreatingGamePresentPressed] = useState(false);
  const {
    createGameFromTemplate,
    isCreatingGameFromTemplate,
  } = useCreateGameFromTemplate();

  useEffect(() => {
    return () => {
      setIsCreatingGameCurimotePressed(false);
      setIsCreatingGamePresentPressed(false);
    };
  }, []);
  const { tenantId, router } = useRouterMatch();
  const openCurimote = useCallback(
    (gameId?: string | null) => {
      if (gameId) {
        const link = window.open(
          `${window.location.origin}/${tenantId}/games/${gameId}/curimote`,
          'curimote',
          `width=${window.innerWidth * 0.6},height=${window.innerHeight * 0.6}`,
        );
        if (link) {
          link.opener = null;
        }
      }
    },
    [tenantId],
  );
  const routeToLive = useCallback(
    (gameId: string) => {
      router.push(`/${tenantId}/games/${gameId}/live`);
    },
    [router, tenantId],
  );

  const presentLesson = useCallback(
    async ({
      template,
      fromBeginning,
      method,
      gameId,
      callbackBeforeRedirect,
    }: {
      template: Template;
      gameId?: string | null;
      method: 'present' | 'curimote';
      fromBeginning?: boolean;
      callbackBeforeRedirect?: (gameId: string) => void;
    }) => {
      // private and workspace present
      if (template.content.slides && template.content.slides.length > 0) {
        if (method === 'curimote') setIsCreatingGameCurimotePressed(true);
        if (method === 'present') setIsCreatingGamePresentPressed(true);
        if (gameId) {
          // saves the first round as currentRound before routing;
          if (fromBeginning) {
            const title = getTitle({
              title: template.title,
              slides: template.content.slides,
            });
            await autoSaveGame({
              gameId,
              currentRound: template.content.slides[0].id,
              modifiedAt: Date.now(),
              isShowingLobby: true,
              slides: template.content.slides,
              title: title,
              attachments: template.attachments,
              requireRealNames: template.requireRealNames,
              isContentFilterEnabled: template.isContentFilterEnabled,
              settings: template.settings,
              // A game created before the lesson carried a descriptor (or before
              // `/presentations/new` forwarded one) has an empty metadata bag.
              // editGameConfig MERGES metadata, so this heals the existing game
              // rather than overwriting anything.
              metadata: lessonGameMetadata(template.metadata),
            });
            if (method === 'curimote' && isDesktop) {
              openCurimote(gameId);
            }
            return routeToLive(gameId);
          }

          // Resume: routes the user to live without touching the game, because
          // the live deck may have diverged from the lesson (slides added during
          // live, wasPlayed flags) and must not be overwritten from the template.
          //
          // The one thing worth writing is the lesson's metadata bag, for the
          // same reason as the restart path above: a game created before the
          // descriptor existed is invisible to the gaps dashboard, and resume is
          // otherwise the one launch path that never heals it. Metadata ONLY —
          // no slides, no currentRound — so resume semantics are unchanged;
          // `editGameConfig` merges the bag and leaves the deck alone.
          //
          // NOT awaited, and failure is swallowed. Resume is what a teacher
          // presses mid-lesson with a class watching, and `autoSaveGame` fetches
          // the game when it isn't cached — which is the usual case from the
          // home screen. Awaiting it would put a network round trip in front of
          // the redirect, and a throw would strand them: nothing here catches,
          // and the button's pressed state only resets on unmount, so they would
          // be left staring at a spinner instead of their lesson. Healing a
          // dashboard field is never worth that. A miss self-corrects on the
          // next resume.
          const resumeMetadata = lessonGameMetadata(template.metadata);
          if (resumeMetadata) {
            void autoSaveGame({
              gameId,
              metadata: resumeMetadata,
              modifiedAt: Date.now(),
            }).catch(() => undefined);
          }

          if (method === 'curimote' && isDesktop) {
            openCurimote(template.content.activeGame?.id);
          }
          return routeToLive(gameId);
        }

        // There is no game, and we need to create it before routing
        if (
          !template.content.activeGame ||
          (template.content.activeGame && !template.content.activeGame.id)
        ) {
          // Metric: Present attempted without an active game id
          metrics.logEvent('Creator.Present.NoActiveGame', {
            templateId: template.id,
          });
          const title = getTitle({
            title: template.title,
            slides: template.content.slides,
          });
          const roundToStartFrom = template.currentRound
            ? template.currentRound
            : template.content.slides?.[0].id;
          createGameFromTemplate({
            activeTenantId: tenantId,
            templateConfig: {
              title: title,
              id: template.id,
              slides: template.content.slides,
              attachments: template.attachments,
              metadata: template.metadata,
              modifiedAt: new Date().getTime(),
              requireRealNames: template.requireRealNames,
              isContentFilterEnabled: template.isContentFilterEnabled,
              currentRound: roundToStartFrom,
              settings: template.settings,
            },
            onSuccessFunction: (gameId: string) => {
              if (callbackBeforeRedirect) {
                callbackBeforeRedirect(gameId);
              }
              if (method === 'curimote' && isDesktop) {
                openCurimote(gameId);
              }
              return routeToLive(gameId);
            },
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      autoSaveGame,
      createGameFromTemplate,
      isDesktop,
      openCurimote,
      routeToLive,
      tenantId,
    ],
  );

  return {
    presentLesson,
    isCreatingGame:
      isCreatingGameFromTemplate ||
      isCreatingGameCurimotePressed ||
      isCreatingGamePresentPressed,
    isCreatingGameCurimote: isCreatingGameCurimotePressed,
    isCreatingGamePresent: isCreatingGamePresentPressed,
  };
};

export default usePresent;
